import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    inject,
    Input,
    OnInit,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { RouterModule } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { combineLatest, map, Observable, of, switchMap, tap } from 'rxjs';
import { first } from 'rxjs/internal/operators/first';
import { RouteConstants } from '../../../../core/constants/routes';
import { openUrlInNewTab } from '../../../../core/functions/window-helper';
import { LoggedInUser } from '../../../../core/models/logged-in-user';
import { AuthFlowService } from '../../../../core/services/auth-wrapper/auth-flow.service';
import { PublicAppConfiguration } from '../../../../core/services/public-app-configuration/models/public-app-configuration';
import { PublicAppConfigurationService } from '../../../../core/services/public-app-configuration/public-app-configuration.service';
import { UserFormattingService } from '../../../../core/services/user-formatting/user-formatting.service';
import { MatMenuService } from '../../../../core/services/utilities/menu-hover.service';
import { EllipsisPipe } from '../../../pipes/ellipsis.pipe';
import { LoggedInUserService } from '../shared/services/logged-in-user/logged-in-user.service';

@Component({
    selector: 'app-header-dropdown',
    imports: [
        MatIcon,
        CommonModule,
        MatMenuModule,
        MatButtonModule,
        EllipsisPipe,
        RouterModule,
    ],
    providers: [AuthService],
    templateUrl: './header-dropdown.component.html',
    styleUrl: './header-dropdown.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class HeaderDropdownComponent implements OnInit {
    private readonly auth0Service: AuthService = inject(AuthService);
    private readonly authFlowService: AuthFlowService = inject(AuthFlowService);
    private readonly matMenuService: MatMenuService = inject(MatMenuService);
    private readonly loggedInUserService: LoggedInUserService =
        inject(LoggedInUserService);
    private readonly userFormattingService: UserFormattingService = inject(
        UserFormattingService,
    );
    private readonly publicAppConfigurationService: PublicAppConfigurationService =
        inject(PublicAppConfigurationService);
    public readonly document: Document = inject(DOCUMENT);

    user$ = this.auth0Service.user$;
    userName$: Observable<string> = of('');
    profileRoute = RouteConstants.Profile.getFullRoute('INDEX');

    @Input() isMobile!: boolean;

    ngOnInit() {
        this.initializeUserName();
    }

    mouseEnter(trigger: MatMenuTrigger) {
        this.matMenuService.openMenu(trigger);
    }

    mouseLeave(trigger: MatMenuTrigger) {
        this.matMenuService.closeMenuWithDelay(trigger);
    }

    login() {
        this.authFlowService.login();
    }

    tools() {
        this.publicAppConfigurationService
            .getPublicAppConfiguration()
            .pipe(
                tap((config: PublicAppConfiguration) => {
                    openUrlInNewTab(`${config.cevoToolsUrl}/tools`);
                }),
            )
            .subscribe();
    }

    register() {
        this.authFlowService.register();
    }

    logout() {
        this.authFlowService.logout();
    }

    private initializeUserName() {
        const loggedInUserName$ = this.user$.pipe(
            first((user) => !!user),
            switchMap(() =>
                this.loggedInUserService
                    .getLoggedInUserInformation()
                    .pipe(map((executor) => this.getDisplayName(executor))),
            ),
        );

        this.userName$ = combineLatest([loggedInUserName$, this.user$]).pipe(
            map(([executorName, user]) => executorName || user?.name || ''),
        );
    }

    private getDisplayName(loggedInUser: LoggedInUser): string {
        return loggedInUser
            ? this.userFormattingService.getDisplayName(
                  loggedInUser.firstName,
                  loggedInUser.lastName,
              )
            : '';
    }
}
